import { InputAdornment, Typography } from "@mui/material";
import { OLButton } from "OLButton";
import { OLPaper } from "OLPaper";
import { OLClearIcon } from "components/CustomIcons/OLClearIcon";
import { DayMonthInput } from "components/FormControls/DayMonthInput/DayMonthInput";
import { NumberFormatInput } from "components/FormControls/NumberFormatInput";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { SelectInput } from "components/FormControls/Select/Select";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { DowngradeTypeFE } from "types/tierSets";
import { getEnvVariable } from "utilities/env";
import { downgradeOptions } from "../utils";
import { useStyles } from "./TierSetForm.styles";

interface Props {
  setFieldValue: (field: string, value?: string | string[]) => void;
  downgradeMode: DowngradeTypeFE;
}

export const TierSetDowngradeConditions = ({
  setFieldValue,
  downgradeMode,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const isAnnualRecalculationOnChosenDate =
    downgradeMode?.period === "annual_recalculation_on_chosen_dates";
  const isEveryXMonths =
    downgradeMode.period === "recalculation_every_month_after_tier_promotion";
  const maxRecalculationDates =
    Number(
      getEnvVariable("REACT_APP_TIER_SET_ANNUAL_RECALCULATION_DATES_LIMIT"),
    ) || 6;

  return (
    <OLPaper className={classes.OLPaper}>
      <SelectInput
        width="100%"
        data-test-id="recalculation-settings"
        label={
          <RequiredFieldLabel text={message({ id: "recalculationSettings" })} />
        }
        name="downgrade.mode"
        items={downgradeOptions.map(({ id, label }) => ({
          id,
          label: message({ id: label }),
        }))}
        onChange={(_, value) => {
          switch (value) {
            case "anniversaryRegistrationDate":
              setFieldValue("downgrade.mode", "anniversaryRegistrationDate");
              setFieldValue("downgrade.period", "registration_anniversary");
              break;
            case "annualChosenDate":
              setFieldValue("downgrade.mode", "annualChosenDate");
              setFieldValue(
                "downgrade.period",
                "annual_recalculation_on_chosen_dates",
              );
              setFieldValue("downgrade.recalculationDates", [""]);
              break;
            case "everyXMonths":
              setFieldValue("downgrade.mode", "everyXMonths");
              setFieldValue(
                "downgrade.period",
                "recalculation_every_month_after_tier_promotion",
              );
              break;
            default:
              setFieldValue("downgrade.mode", value);
              setFieldValue("downgrade.period", undefined);
          }
        }}
        isCustomSelect
      />
      {isAnnualRecalculationOnChosenDate && (
        <FieldArray
          name="downgrade.recalculationDates"
          render={({ remove, push, form: { values } }) => {
            const isAddingMoreDatesDisabled =
              values.downgrade.recalculationDates?.length >=
              maxRecalculationDates;

            return (
              <div>
                {values.downgrade.recalculationDates.map(
                  (_input: string[], index: number) => {
                    return (
                      <div key={index} className={classes.dateWrapper}>
                        <DayMonthInput
                          name={`downgrade.recalculationDates.${index}`}
                          label={
                            index === 0 ? (
                              <RequiredFieldLabel
                                text={message({ id: "date" })}
                              />
                            ) : (
                              message({ id: "date" })
                            )
                          }
                        />
                        {index === 0 ? null : (
                          <OLClearIcon
                            data-test-id="clear"
                            onClick={() => remove(index)}
                          />
                        )}
                      </div>
                    );
                  },
                )}

                <Typography className={classes.anniversaryInfo}>
                  {message({ id: "annualRecalculationOnChosenDateInfo" })}
                </Typography>
                <OLButton
                  onClick={() => push("")}
                  variant="outlined"
                  disabled={isAddingMoreDatesDisabled}
                >
                  {message({ id: "addNewDate" })}
                </OLButton>
              </div>
            );
          }}
        />
      )}
      {isEveryXMonths ? (
        <NumberFormatInput
          name="downgrade.interval"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {message({ id: "months" }).toLowerCase()}
              </InputAdornment>
            ),
          }}
          label={
            <RequiredFieldLabel text={message({ id: "downgradeEvery" })} />
          }
          className={classes.input}
          data-test-id="downgrade-every"
        />
      ) : null}
    </OLPaper>
  );
};
